import { useState, useEffect } from 'react';
import { Outlet, Link, useLocation } from 'react-router-dom';
import { useI18n } from './i18n';
import { Globe, ArrowRight, Menu, X } from 'lucide-react';

// Feedback haptique léger (Android / Chrome mobile).
// Silencieusement ignoré sur iOS Safari qui n'implémente pas l'API.
const haptic = (duration = 10) => {
  try {
    if (typeof navigator !== 'undefined' && typeof navigator.vibrate === 'function') {
      navigator.vibrate(duration);
    }
  } catch (_) {
    /* no-op */
  }
};

const NavLink = ({ to, children, onClick }) => {
  const location = useLocation();
  const active = location.pathname === to || (to !== '/' && location.pathname.startsWith(to));
  return (
    <Link
      to={to}
      onClick={onClick}
      className={`text-sm font-medium transition-colors duration-200 ${
        active ? 'text-[#34B2C8]' : 'text-slate-600 hover:text-slate-900'
      }`}
    >
      {children}
    </Link>
  );
};

const MobileLink = ({ to, onClick, children }) => {
  const location = useLocation();
  const active = location.pathname === to || (to !== '/' && location.pathname.startsWith(to));
  const handleClick = (e) => {
    haptic(8);
    if (onClick) onClick(e);
  };
  return (
    <Link
      to={to}
      onClick={handleClick}
      className={`block px-6 py-4 text-base font-medium border-b border-slate-100 transition-colors active:bg-slate-50 ${
        active ? 'text-[#34B2C8]' : 'text-slate-700 hover:text-[#34B2C8]'
      }`}
    >
      {children}
    </Link>
  );
};

export const LandingLayout = ({ basePath = '/landing' }) => {
  const { t, lang, toggleLang } = useI18n();
  const location = useLocation();
  const [menuOpen, setMenuOpen] = useState(false);

  // basePath peut être "" (mode vitrine à la racine) ou "/landing" (app principale).
  // On normalise pour les liens : home == "/" + préfixe des sous-pages.
  const home = basePath || '/';
  const prefix = basePath; // "" ou "/landing"

  // Ferme le menu automatiquement lors d'un changement de route
  useEffect(() => {
    setMenuOpen(false);
  }, [location.pathname]);

  // Empêche le scroll du body quand le menu est ouvert
  useEffect(() => {
    if (menuOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => { document.body.style.overflow = ''; };
  }, [menuOpen]);

  const closeMenu = () => setMenuOpen(false);

  return (
    <div className="min-h-screen bg-white" style={{ fontFamily: "'Manrope', sans-serif" }}>
      <link rel="preconnect" href="https://fonts.googleapis.com" />
      <link href="https://fonts.googleapis.com/css2?family=Outfit:wght@300;500;700&family=Manrope:wght@400;500;600&display=swap" rel="stylesheet" />

      {/* Sticky Navbar */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-white/70 backdrop-blur-xl border-b border-slate-200/60">
        <div className="max-w-7xl mx-auto px-6 h-16 flex items-center justify-between">
          <Link to={home} className="flex items-center gap-3 mr-8 flex-shrink-0" data-testid="nav-logo">
            <img
              src="https://customer-assets.emergentagent.com/job_5cdbc63d-d5b1-4e79-aca3-cba0bfe3b8fa/artifacts/uwhw7jb6_Logo%20Anticiper.png"
              alt="Anticiper"
              className="h-9 w-9 object-contain"
            />
            <span className="text-lg font-semibold text-slate-900" style={{ fontFamily: "'Outfit', sans-serif" }}>
              Anticiper
            </span>
          </Link>

          {/* Desktop nav */}
          <div className="hidden md:flex items-center gap-6 whitespace-nowrap">
            <NavLink to={home}>{t.nav.home}</NavLink>
            <NavLink to={`${prefix}/formation`}>{t.nav.formation}</NavLink>
            <NavLink to={`${prefix}/scenarios`}>{t.nav.scenarios}</NavLink>
            <NavLink to={`${prefix}/demo`}>{t.nav.demo}</NavLink>
            <NavLink to={`${prefix}/about`}>{t.nav.about}</NavLink>
          </div>

          <div className="flex items-center gap-2 md:gap-3">
            <button
              onClick={toggleLang}
              data-testid="lang-toggle"
              className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-slate-500 hover:text-slate-800 border border-slate-200 rounded-full transition-colors duration-200"
            >
              <Globe size={14} />
              {lang === 'fr' ? 'EN' : 'FR'}
            </button>
            <Link
              to={`${prefix}/contact`}
              data-testid="nav-cta"
              className="hidden md:inline-flex items-center gap-2 bg-[#34B2C8] hover:bg-[#2a9ab0] text-white text-sm font-medium px-5 py-2 rounded-full transition-all duration-200 hover:scale-[1.03]"
            >
              {t.nav.cta}
              <ArrowRight size={14} />
            </Link>

            {/* Mobile burger button */}
            <button
              onClick={() => {
                haptic(15);
                setMenuOpen(v => !v);
              }}
              data-testid="mobile-menu-toggle"
              aria-label={menuOpen ? 'Fermer le menu' : 'Ouvrir le menu'}
              aria-expanded={menuOpen}
              className="md:hidden flex items-center justify-center w-10 h-10 rounded-full text-slate-700 hover:bg-slate-100 active:bg-slate-200 transition-colors"
            >
              {menuOpen ? <X size={22} /> : <Menu size={22} />}
            </button>
          </div>
        </div>
      </nav>

      {/* Mobile menu overlay + panel */}
      <div
        onClick={closeMenu}
        className={`md:hidden fixed inset-0 z-40 bg-slate-900/40 backdrop-blur-sm transition-opacity duration-300 ${
          menuOpen ? 'opacity-100 pointer-events-auto' : 'opacity-0 pointer-events-none'
        }`}
        aria-hidden="true"
      />
      <div
        data-testid="mobile-menu"
        className={`md:hidden fixed top-16 left-0 right-0 z-40 bg-white border-b border-slate-200 shadow-lg transition-all duration-300 origin-top ${
          menuOpen ? 'opacity-100 translate-y-0 pointer-events-auto' : 'opacity-0 -translate-y-2 pointer-events-none'
        }`}
      >
        <div className="flex flex-col">
          <MobileLink to={home} onClick={closeMenu}>{t.nav.home}</MobileLink>
          <MobileLink to={`${prefix}/formation`} onClick={closeMenu}>{t.nav.formation}</MobileLink>
          <MobileLink to={`${prefix}/scenarios`} onClick={closeMenu}>{t.nav.scenarios}</MobileLink>
          <MobileLink to={`${prefix}/demo`} onClick={closeMenu}>{t.nav.demo}</MobileLink>
          <MobileLink to={`${prefix}/about`} onClick={closeMenu}>{t.nav.about}</MobileLink>
          <div className="px-6 py-5">
            <Link
              to={`${prefix}/contact`}
              onClick={() => { haptic(8); closeMenu(); }}
              data-testid="mobile-menu-cta"
              className="flex items-center justify-center gap-2 bg-[#34B2C8] hover:bg-[#2a9ab0] active:scale-[0.98] text-white text-sm font-medium px-5 py-3 rounded-full transition-all duration-200 w-full"
            >
              {t.nav.cta}
              <ArrowRight size={14} />
            </Link>
          </div>
        </div>
      </div>

      {/* Page Content */}
      <main className="pt-16">
        <Outlet context={{ basePath: prefix, home, prefix }} />
      </main>

      {/* Footer */}
      <footer className="bg-slate-50 border-t border-slate-200">
        <div className="max-w-6xl mx-auto px-6 py-16">
          <div className="grid md:grid-cols-3 gap-12">
            <div>
              <div className="flex items-center gap-3 mb-4">
                <img
                  src="https://customer-assets.emergentagent.com/job_5cdbc63d-d5b1-4e79-aca3-cba0bfe3b8fa/artifacts/uwhw7jb6_Logo%20Anticiper.png"
                  alt="Anticiper"
                  className="h-8 w-8 object-contain"
                />
                <span className="text-base font-semibold text-slate-900" style={{ fontFamily: "'Outfit', sans-serif" }}>
                  Anticiper
                </span>
              </div>
              <p className="text-sm text-slate-500 leading-relaxed max-w-xs">
                {t.footer.tagline}
              </p>
            </div>
            <div>
              <h4 className="text-sm font-semibold text-slate-900 mb-4">{t.footer.nav}</h4>
              <div className="flex flex-col gap-2.5">
                <Link to={home} className="text-sm text-slate-500 hover:text-[#34B2C8] transition-colors">{t.nav.home}</Link>
                <Link to={`${prefix}/formation`} className="text-sm text-slate-500 hover:text-[#34B2C8] transition-colors">{t.nav.formation}</Link>
                <Link to={`${prefix}/scenarios`} className="text-sm text-slate-500 hover:text-[#34B2C8] transition-colors">{t.nav.scenarios}</Link>
                <Link to={`${prefix}/demo`} className="text-sm text-slate-500 hover:text-[#34B2C8] transition-colors">{t.nav.demo}</Link>
                <Link to={`${prefix}/about`} className="text-sm text-slate-500 hover:text-[#34B2C8] transition-colors">{t.nav.about}</Link>
                <Link to={`${prefix}/contact`} className="text-sm text-slate-500 hover:text-[#34B2C8] transition-colors">{t.nav.contact}</Link>
              </div>
            </div>
            <div>
              <h4 className="text-sm font-semibold text-slate-900 mb-4">Legal</h4>
              <div className="flex flex-col gap-2.5">
                <Link to={`${prefix}/legal`} className="text-sm text-slate-500 hover:text-[#34B2C8] transition-colors">{t.footer.legal}</Link>
                <Link to={`${prefix}/privacy`} className="text-sm text-slate-500 hover:text-[#34B2C8] transition-colors">{t.footer.privacy}</Link>
              </div>
            </div>
          </div>
          <div className="mt-12 pt-8 border-t border-slate-200">
            <p className="text-xs text-slate-400">{t.footer.copyright}</p>
          </div>
        </div>
      </footer>
    </div>
  );
};
